import { useCallback, useState } from "react";

/**
 * Desktop sidebar collapse state (GT-B-SHELL-NAV1). Expanded is the default; the
 * choice is remembered per browser in localStorage. Storage can be unavailable
 * (privacy mode, quota, disabled cookies), so every access is guarded and a
 * failure falls back to the expanded default for this session.
 */
export const SIDEBAR_COLLAPSED_STORAGE_KEY = "gt.sidebar.collapsed";

export function readSidebarCollapsed(): boolean {
  try {
    return globalThis.localStorage?.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function writeSidebarCollapsed(collapsed: boolean): void {
  try {
    globalThis.localStorage?.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, collapsed ? "1" : "0");
  } catch {
    // Storage unavailable: the in-memory state still works for this session.
  }
}

export function useSidebarCollapsed(): readonly [boolean, () => void] {
  const [collapsed, setCollapsed] = useState<boolean>(readSidebarCollapsed);
  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      writeSidebarCollapsed(next);
      return next;
    });
  }, []);
  return [collapsed, toggle] as const;
}
